import React, { useState } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { UiIcon } from "../Layouts/Icon";
import { ICON_COUNT_TEXT, ICON_LIST_PATH, COMPOSER_PATH, EXAMPLES_PATH, LICENSE_PATH } from "../constants";
import classes from "./not-found.scss";

// "404" on a 5×7 dot matrix: every lit dot is an icon
const DIGITS = {
  4: ["...#.", "..##.", ".#.#.", "#..#.", "#####", "...#.", "...#."],
  0: [".###.", "#...#", "#..##", "#.#.#", "##..#", "#...#", ".###."],
};

// Icons from the site's own set, so the page needs no icon data to render
const ICONS = [
  "home", "search", "heart-eyes", "light-bulb", "dart", "airplane-alt", "beach", "full-sunny",
  "football", "tea", "candy", "book", "video", "at", "color-picker", "clip", "server",
  "clock-time", "options", "squirrel", "circuit", "worker", "dollar", "apple-watch",
  "first-aid", "dumbbells", "law", "chart-pie", "twitter", "mastercard", "file-pdf", "check",
];

// Same picks on the server and in the browser; a shuffle just moves the offset
const iconFor = (index, seed) => ICONS[(index * 7 + seed * 11 + ((index * index) % 13)) % ICONS.length];

const Digit = ({ digit, offset, seed }) => (
  <div className={classes.digit} aria-hidden="true">
    {DIGITS[digit].map((row, y) =>
      row.split("").map((cell, x) => {
        const index = offset + y * 5 + x;
        // Lit dots shade from violet to pink across the whole number
        const style = { "--delay": `${index * 14}ms`, "--hue": 258 + (offset / 35 + x / 15) * 14 };
        return cell === "#" ? (
          <span key={`${y}-${x}`} className={classes.lit} style={style}>
            <UiIcon name={iconFor(index, seed)} />
          </span>
        ) : (
          <span key={`${y}-${x}`} className={classes.dot} style={style} />
        );
      })
    )}
  </div>
);

Digit.propTypes = {
  digit: PropTypes.number.isRequired,
  offset: PropTypes.number.isRequired,
  seed: PropTypes.number.isRequired,
};

export const NotFound = ({ staticContext, history }) => {
  // Lets the server answer with a real 404 status
  if (staticContext) staticContext.status = 404;
  const [seed, setSeed] = useState(0);

  return (
    <main className={classes.page}>
      <Helmet>
        <title>Page not found | IcoFont</title>
        <meta name="robots" content="noindex" />
      </Helmet>

      <Link to="/" className={classes.logo} aria-label="IcoFont home">
        <img src="/images/logo.svg" alt="IcoFont" />
      </Link>

      <div className={classes.stage}>
        <button
          type="button"
          className={classes.number}
          onClick={() => setSeed((value) => value + 1)}
          aria-label="404. Shuffle the icons"
          title="Shuffle the icons"
          key={seed}
        >
          <Digit digit={4} offset={0} seed={seed} />
          <Digit digit={0} offset={35} seed={seed} />
          <Digit digit={4} offset={70} seed={seed} />
        </button>

        <h1 className={classes.title}>This page drew a blank.</h1>
        <p className={classes.text}>
          The link may be broken or the page may have moved. All {ICON_COUNT_TEXT} icons are still right
          where you left them.
        </p>

        <div className={classes.actions}>
          <Link to={ICON_LIST_PATH} className={classes.primary}>
            <UiIcon name="search" /> Browse icons
          </Link>
          <button
            type="button"
            className={classes.secondary}
            onClick={() => (history && history.length > 1 ? history.goBack() : history.push("/"))}
          >
            Go back
          </button>
        </div>

        <nav className={classes.links} aria-label="Helpful links">
          <Link to={COMPOSER_PATH}>Composer</Link>
          <Link to={EXAMPLES_PATH}>Examples</Link>
          <Link to={LICENSE_PATH}>License</Link>
        </nav>
      </div>

      <p className={classes.hint} aria-hidden="true">
        Tip: click or tap the numbers to shuffle the icons
      </p>
    </main>
  );
};

NotFound.propTypes = {
  staticContext: PropTypes.object,
  history: PropTypes.object,
};

export default NotFound;
